import { Cloudinary } from "@cloudinary/url-gen"
import { limitFit } from "@cloudinary/url-gen/actions/resize"
import { set } from "@cloudinary/url-gen/actions/variable"
import { expression } from "@cloudinary/url-gen/qualifiers/expression"
import { auto } from "@cloudinary/url-gen/qualifiers/format"

function normalizePublicId(publicId) {
  if (!publicId || typeof publicId !== "string") return publicId

  if (!publicId.includes("image/upload")) {
    return publicId.replace(/\.(jpg|jpeg|png|webp)$/, "")
  }

  try {
    const afterUpload = publicId.split("/image/upload/")[1]
    if (!afterUpload) return publicId

    const versionMatch = afterUpload.match(/v\d+\/(.+)/)
    if (!versionMatch || !versionMatch[1]) return publicId

    return versionMatch[1].replace(/\.(jpg|jpeg|png|webp)$/, "")
  } catch {
    return publicId
  }
}

// Global server-side caches (attached to the global object to persist across HMR/reloads)
const serverCache = !process.browser ? global.__imageLoaderServerCache__ || (global.__imageLoaderServerCache__ = new Map()) : null

const oneSrcServerCache = !process.browser ? global.__imageLoaderOneSrcServerCache__ || (global.__imageLoaderOneSrcServerCache__ = new Map()) : null

function getCacheKey(src, policy, path, env) {
  const publicId = src && typeof src === "object" ? src.public_id : src
  const version = src && typeof src === "object" ? src.version : ""
  const cloudName = env?.cloudinary?.cloudName || ""
  const secureDistribution = env?.cloudinary?.secureDistribution || ""
  return `${publicId}_${version}_${policy || ""}_${path || ""}_${cloudName}_${secureDistribution}`
}

export default function imageLoader({ src, policy, path }, env) {
  const isServer = !process.browser

  let cacheKey
  if (isServer) {
    cacheKey = getCacheKey(src, policy, path, env)
    if (serverCache.has(cacheKey)) {
      return serverCache.get(cacheKey)
    }
  }

  src = src && src.constructor === Object ? src : { public_id: src || "/", version: 1 }

  if (typeof src.public_id === "string" && src.public_id.includes("image/upload")) {
    try {
      const cleanUrl = new URL(src.public_id, `https://${env?.cloudinary?.secureDistribution || `res.cloudinary.com/${env?.cloudinary?.cloudName}`}`)
      cleanUrl.searchParams.delete("_a")
      const cleanUrlFunc = () => cleanUrl
      if (isServer) {
        serverCache.set(cacheKey, cleanUrlFunc)
      }
      return cleanUrlFunc
    } catch {
      const srcIdFunc = () => src.public_id
      if (isServer) {
        serverCache.set(cacheKey, srcIdFunc)
      }
      return srcIdFunc
    }
  }

  src.public_id = normalizePublicId(src.public_id)

  const cld = new Cloudinary({
    cloud: { cloudName: env?.cloudinary?.cloudName },
    url: Object.assign(
      { privateCdn: !!env?.cloudinary?.secureDistribution },
      !!env?.cloudinary?.secureDistribution && { secureDistribution: env?.cloudinary?.secureDistribution }
    ),
  })

  // Local URL caches in generator closure
  const urlCache = {}
  let baseUrlTemplate = null

  const generator = ({ width, quality = "auto:eco", blur }) => {
    const localKey = `${width || ""}_${quality || ""}_${blur || ""}`
    if (urlCache[localKey]) {
      return urlCache[localKey]
    }

    try {
      if (!width) {
        // Fallback: If no width is specified, use original slow path (rare / single call)
        const image = cld.image(src.public_id)
        if (src.version) image.setVersion(src.version)
        if (quality) image.quality(quality)
        image.format(auto())
        if (path) image.addVariable(set("path", expression("!" + path + "!")))
        if (policy) image.namedTransformation(policy)
        let url = image.toURL()
        if (blur) url = url.replace("/f_auto", `/e_blur:${blur},f_auto`)
        const cleanUrl = new URL(url)
        cleanUrl.searchParams.delete("_a")
        urlCache[localKey] = cleanUrl
        return cleanUrl
      }

      if (!baseUrlTemplate) {
        const image = cld.image(src.public_id)
        if (src.version) image.setVersion(src.version)
        if (quality) image.quality(quality)
        image.format(auto())
        if (path) image.addVariable(set("path", expression("!" + path + "!")))
        if (policy) image.namedTransformation(policy)
        image.resize(limitFit().width("__WIDTH__"))

        let url = image.toURL()
        if (blur) url = url.replace("/f_auto", `/e_blur:${blur},f_auto`)

        const cleanUrl = new URL(url)
        cleanUrl.searchParams.delete("_a")
        baseUrlTemplate = cleanUrl.toString()
      }

      const cleanUrl = new URL(baseUrlTemplate.replace("__WIDTH__", width))
      urlCache[localKey] = cleanUrl
      return cleanUrl
    } catch (error) {
      return null
    }
  }

  if (isServer) {
    if (serverCache.size > 10000) serverCache.clear()
    serverCache.set(cacheKey, generator)
  }

  return generator
}

export function imageLoaderOneSrc({ src, policy, path }, env) {
  const isServer = !process.browser

  let cacheKey
  if (isServer) {
    cacheKey = getCacheKey(src, policy, path, env)
    if (oneSrcServerCache.has(cacheKey)) {
      return oneSrcServerCache.get(cacheKey)
    }
  }

  src = src && src.constructor === Object ? src : { public_id: src || "/", version: 1 }

  src.public_id = normalizePublicId(src.public_id)

  const cld = new Cloudinary({
    cloud: { cloudName: env?.cloudinary?.cloudName },
    url: Object.assign(
      { privateCdn: !!env?.cloudinary?.secureDistribution },
      !!env?.cloudinary?.secureDistribution && { secureDistribution: env?.cloudinary?.secureDistribution }
    ),
  })

  // Local URL caches in generator closure
  const urlCache = {}

  const generator = ({ quality = "auto:best", blur, format = false } = {}) => {
    const localKey = `${quality}_${blur || ""}_${format}`
    if (urlCache[localKey]) {
      return urlCache[localKey]
    }

    try {
      const image = cld.image(src.public_id)

      if (src.version) {
        image.setVersion(src.version)
      }

      if (quality) {
        image.quality(quality)
      }

      if (format) {
        image.format(auto())
      }

      if (path) {
        image.addVariable(set("path", expression("!" + path + "!")))
      }

      if (policy) {
        image.namedTransformation(policy)
      }

      let url = image.toURL()

      if (blur) {
        url = url.replace("/f_auto", `/e_blur:${blur},f_auto`)
      }

      const cleanUrl = new URL(url)
      cleanUrl.searchParams.delete("_a")

      urlCache[localKey] = cleanUrl
      return cleanUrl
    } catch {
      return null
    }
  }

  if (isServer) {
    if (oneSrcServerCache.size > 10000) oneSrcServerCache.clear()
    oneSrcServerCache.set(cacheKey, generator)
  }

  return generator
}
